import { Address, AuthUser, IShopifyAddress } from '@/context/types';

interface LaterOptions {
  signal?: AbortSignal;
}

/**
 * Resolves after a specified delay.
 * * @param delay - The time to wait in milliseconds.
 * @param options - Optional configuration, including an AbortSignal for cancellation.
 * @returns A promise that resolves when the delay is complete, or rejects if aborted/invalid.
 */
export function later(delay: number, options?: LaterOptions): Promise<void> {
  // 1. Input Validation: Prevent NaN, Infinity, or negative delays
  if (delay < 0 || !Number.isFinite(delay)) {
    return Promise.reject(
      new TypeError('Delay must be a finite, positive number.'),
    );
  }

  // 2. Early Exit: If the signal is already aborted, don't even start
  if (options?.signal?.aborted) {
    return Promise.reject(new DOMException('Delay aborted', 'AbortError'));
  }

  return new Promise((resolve, reject) => {
    // 3. Type-safe timeout ID (handles both Node/Vite and Browser environments)
    const timerId: ReturnType<typeof setTimeout> = setTimeout(() => {
      cleanup();
      resolve();
    }, delay);

    // 4. Handle cancellation mid-flight
    const onAbort = () => {
      cleanup();
      reject(new DOMException('Delay aborted', 'AbortError'));
    };

    // 5. Memory management: Always clean up timers and listeners
    const cleanup = () => {
      clearTimeout(timerId);
      if (options?.signal) {
        options.signal.removeEventListener('abort', onAbort);
      }
    };

    // 6. Attach the abort listener if a signal was provided
    if (options?.signal) {
      options.signal.addEventListener('abort', onAbort);
    }
  });
}

// Utility to ensure the redirect is a safe, relative path within your application.
// This prevents external Open Redirect attacks.
export const getSafeRedirectUrl = (url: string | null): string => {
  if (
    !url ||
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('//')
  ) {
    return '/';
  }
  return url;
};

export const splitFullName = (
  fullName: string,
): { firstName: string; lastName: string } => {
  const nameParts = fullName.trim()?.split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '—';

  return { firstName, lastName };
};

export const formatSecondaryAddress = ({
  secondary_address = '',
  aptSuite = '',
}: {
  secondary_address?: string;
  aptSuite?: string;
}) => [secondary_address, aptSuite].filter(Boolean).join(', ');

/**
 * Formats address components into a standard US string.
 * Expected format:
 * Street, Secondary
 * City, ST Zip
 */
export const formatUSAddress = ({
  street,
  secondary_address,
  place,
  region_code,
  postcode,
}: Partial<Address>) => {
  // Combine street and secondary (e.g., "123 Main St, Apt 4")
  const line1 = [street, secondary_address].filter(Boolean).join(', ');

  // Combine city, state code, and zip (e.g., "New York, NY 10001")
  const line2 =
    `${place ? `${place}, ` : ''}${region_code || ''} ${postcode || ''}`.trim();

  return [line1, line2].filter(Boolean).join('\n');
};

const transformShopifyToAppAddress = (
  shopifyAddress: Partial<IShopifyAddress> = {},
) => {
  return {
    street: shopifyAddress?.address1 || '',
    secondary_address: shopifyAddress?.address2 || '',
    postcode: shopifyAddress?.zip || '',
    place: shopifyAddress?.city || '',
    region: shopifyAddress?.province || '',
    region_code: shopifyAddress?.provinceCode || '',
  };
};

export const parseDefaultAddress = (
  shopifyAddress: Partial<IShopifyAddress> = {},
) => {
  if (!shopifyAddress?.address1 && shopifyAddress?.id) return null;
  return transformShopifyToAppAddress(shopifyAddress);
};

// Helper to extract numeric value from Shopify's JSON-string metafields
export const parseJSONMeta = (field: any) => {
  if (!field?.value) return 0;
  try {
    const parsed = JSON.parse(field.value);
    return parsed.value ?? 0;
  } catch {
    return parseFloat(field.value) || 0;
  }
};
